import React, { useEffect, useState, useRef } from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';

const Process = () => {
  const data = useLandingPageData();
  const { processSteps, features } = data;
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);

  // Different glow colors that complement the theme
  const glowColors = [
    { primary: '#7BA7C2', secondary: '#9BBFD9' },
    { primary: '#5A8BA6', secondary: '#7BA7C2' },
    { primary: '#9BBFD9', secondary: '#7BA7C2' },
    { primary: '#7BA7C2', secondary: '#5A8BA6' },
    { primary: '#5A8BA6', secondary: '#9BBFD9' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const section = sectionRef.current;
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      // Start very early (1.5x viewport height before section)
      const start = sectionTop - windowHeight * 1.5;
      // End earlier (when section is 30% from top)
      const end = sectionTop + sectionHeight - windowHeight * 0.7;
      
      if (scrollY < start) {
        setScrollProgress(0);
      } else if (scrollY > end) {
        setScrollProgress(100);
      } else {
        const progress = ((scrollY - start) / (end - start)) * 100;
        setScrollProgress(Math.min(100, Math.max(0, progress)));
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide when DHI-specific process is used (after all hooks)
  if (features?.showDhiProcess) return null;

  // Direct text translations for variant-specific process steps
  const titleMap = {
    'Kostenlose Beratung': 'Free Consultation',
    'Detaillierte Planung': 'Detailed Planning',
    'Die Behandlung': 'The Treatment',
    'Nachsorge': 'Aftercare',
    'Das Ergebnis': 'The Result',
    'Beratung & Design': 'Consultation & Design',
    'Vorzeichnung': 'Pre-Drawing',
    'Transplantation': 'Transplantation',
    'Heilungsphase': 'Healing Phase',
    'Ihr Ergebnis': 'Your Result',
  };
  const descMap = {
    'Vereinbaren Sie einen Termin für Ihre persönliche Bartanalyse - online oder vor Ort in unserem Standort in Mönchengladbach.':
      'Schedule an appointment for your personal beard analysis - online or on-site at our location in Mönchengladbach.',
    'Wir analysieren Ihre Bartstruktur und besprechen die optimale Anzahl der Grafts sowie die gewünschte Bartform.':
      'We analyze your beard structure and discuss the optimal number of grafts and your desired beard shape.',
    'Am Behandlungstag werden Sie von unserem erfahrenen Team betreut. Die Prozedur dauert je nach Umfang 3-6 Stunden unter lokaler Betäubung.':
      'On the treatment day, you will be cared for by our experienced team. The procedure takes 3-6 hours under local anesthesia.',
    'Wir geben Ihnen detaillierte Pflegeanweisungen und sind für alle Ihre Fragen da. Regelmäßige Kontrolltermine sichern optimale Ergebnisse.':
      'We provide detailed care instructions and are available for all your questions. Regular follow-up appointments ensure optimal results.',
    'Nach 12-18 Monaten sehen Sie das vollständige Ergebnis: einen vollen, natürlich aussehenden Bart.':
      'After 12-18 months, you will see the complete result: a full, natural-looking beard.',
    'Wir analysieren Ihre Gesichtsform und entwerfen gemeinsam die perfekte Augenbrauenform für Sie.':
      'We analyze your face shape and design the perfect eyebrow shape together with you.',
    'Am Behandlungstag zeichnen wir die neue Brauenform vor und Sie können sie vor dem Eingriff freigeben.':
      'On the treatment day, we draw the new brow shape and you can approve it before the procedure.',
    'Feine Haarfollikel werden aus dem Hinterkopf entnommen und einzeln in die Augenbrauenregion implantiert.':
      'Fine hair follicles are extracted from the back of the head and implanted individually into the eyebrow area.',
    'Kleine Krusten fallen nach 7-10 Tagen ab. Die Augenbrauen sehen dann bereits voller aus.':
      'Small crusts fall off after 7-10 days. The eyebrows already look fuller.',
    'Nach 6-12 Monaten genießen Sie volle, natürliche Augenbrauen, die Sie normal stylen können.':
      'After 6-12 months, enjoy full, natural eyebrows that you can style normally.',
    'Vereinbaren Sie einen unverbindlichen Termin für eine professionelle Haaranalyse - online oder vor Ort in unserem Standort in Mönchengladbach.':
      'Schedule a no-obligation appointment for a professional hair analysis - online or on-site at our location in Mönchengladbach.',
    'Unser Spezialist erstellt einen detaillierten Plan mit Graft-Anzahl, Haarlinie und individuellen Wünschen.':
      'Our specialist creates a detailed plan with graft count, hairline and individual wishes.',
    'Am Behandlungstag werden Sie von unserem erfahrenen Team betreut. Die Prozedur dauert je nach Umfang 4-8 Stunden.':
      'On the treatment day, you will be cared for by our experienced team. The procedure takes 4-8 hours depending on scope.',
    'Sie erhalten detaillierte Pflegeanweisungen. Regelmäßige Kontrolltermine sichern optimale Ergebnisse.':
      'You receive detailed care instructions. Regular check-ups ensure optimal results.',
    'Nach 12-18 Monaten genießen Sie das vollständige Ergebnis: natürlich aussehendes, dichtes Haar.':
      'After 12-18 months, enjoy the complete result: natural-looking, dense hair.',
  };
  const getTitle = (step) => language === 'en' && titleMap[step.title] ? titleMap[step.title] : step.title;
  const getDesc = (step) => language === 'en' && descMap[step.description] ? descMap[step.description] : step.description;

  return (
    <section id="ablauf" ref={sectionRef} className={`py-24 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
    }`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/5' : 'bg-[#5A8BA6]/5'
        }`} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
          }`}>
            {t('process.sectionBadge')}
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Der ' : 'The '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Ablauf' : 'Process'}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('process.sectionDescription')}
          </p>
        </div>

        {/* Process Steps - Desktop */}
        <div className="hidden lg:block">
          {/* Steps Row with Circles */}
          <div className="flex items-start justify-between">
            {processSteps.map((step, index) => {
              // Consistent color for all numbers in desktop view (same as step 1)
              const desktopColor = '#7BA7C2';
              
              const translatedTitle = getTitle(step);
              const translatedDesc = getDesc(step);
              
              return (
                <div key={step.step} className="relative group flex flex-col items-center flex-1">
                  {/* Number Row */}
                  <div className="relative mb-12 flex items-center justify-center w-full">
                    
                    {/* Line AFTER this circle (except last) */}
                    {index < processSteps.length - 1 && (
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 h-[2px] left-[calc(50%+35px)] right-[calc(-50%+35px)]"
                        style={{ 
                          background: isDark 
                            ? 'linear-gradient(to right, transparent, rgba(123, 167, 194, 0.35) 25%, rgba(123, 167, 194, 0.35) 75%, transparent)'
                            : 'linear-gradient(to right, transparent, rgba(90, 139, 166, 0.25) 25%, rgba(90, 139, 166, 0.25) 75%, transparent)'
                        }}
                      />
                    )}

                    {/* Circle with Glow */}
                    <div className="relative z-10">
                      {/* Outer glow ring - animated */}
                      <div 
                        className="absolute -inset-3 rounded-full opacity-50 blur-lg animate-pulse-slow"
                        style={{ 
                          background: `radial-gradient(circle, ${desktopColor} 0%, transparent 70%)`,
                          animationDelay: `${index * 0.3}s`
                        }}
                      />
                      {/* Inner glow layer */}
                      <div 
                        className="absolute -inset-1.5 rounded-full opacity-70 blur-md animate-glow"
                        style={{ 
                          background: `radial-gradient(circle, ${desktopColor} 0%, transparent 70%)`,
                          animationDelay: `${index * 0.2}s`
                        }}
                      />
                      {/* Number container */}
                      <div 
                        className="relative w-14 h-14 rounded-full flex items-center justify-center border-2 transition-all duration-500 group-hover:scale-110"
                        style={{ 
                          borderColor: `${desktopColor}60`,
                          background: isDark ? '#0f0f0f' : '#f8f9fa',
                          boxShadow: `0 0 20px ${desktopColor}40`
                        }}
                      >
                        <span 
                          className="text-2xl font-bold"
                          style={{ 
                            color: desktopColor,
                            textShadow: `0 0 15px ${desktopColor}`
                          }}
                        >
                          {step.step}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="text-center px-4">
                    <h3 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${
                      isDark ? 'text-white group-hover:text-[#7BA7C2]' : 'text-gray-900 group-hover:text-[#5A8BA6]'
                    }`}>
                      {translatedTitle}
                    </h3>
                    <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                      {translatedDesc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Process Steps - Mobile/Tablet */}
        <div className="lg:hidden relative">
          <div className="space-y-0">
            {processSteps.map((step, index) => {
              const colors = glowColors[index];
              // More evenly distributed activation - each step gets equal portion
              const stepThreshold = ((index + 0.5) / processSteps.length) * 100;
              const isActive = scrollProgress >= stepThreshold - 15;
              
              const segmentStart = (index / processSteps.length) * 100;
              const segmentEnd = ((index + 1) / processSteps.length) * 100;
              const segmentProgress = scrollProgress <= segmentStart ? 0 
                : scrollProgress >= segmentEnd ? 100 
                : ((scrollProgress - segmentStart) / (segmentEnd - segmentStart)) * 100;
              
              // Get translated process step data for mobile
              const translatedTitleMobile = getTitle(step);
              const translatedDescMobile = getDesc(step);
              
              return (
                <div
                  key={step.step}
                  className="relative flex gap-6 group"
                >
                  {/* Number Column */}
                  <div className="relative flex flex-col items-center flex-shrink-0" style={{ width: '44px' }}>
                    
                    {/* Circle with Glow - shifted down to align with text center */}
                    <div className="relative mt-3">
                      {/* Outer glow */}
                      <div 
                        className={`absolute -inset-2 rounded-full blur-lg transition-opacity duration-700 ${isActive ? 'opacity-60' : 'opacity-20'}`}
                        style={{ 
                          background: `radial-gradient(circle, ${colors.primary} 0%, transparent 70%)`
                        }}
                      />
                      {/* Inner glow */}
                      <div 
                        className={`absolute -inset-1 rounded-full blur-md transition-opacity duration-700 ${isActive ? 'opacity-70' : 'opacity-30'}`}
                        style={{ 
                          background: `radial-gradient(circle, ${colors.secondary} 0%, transparent 70%)`
                        }}
                      />
                      {/* Number container */}
                      <div 
                        className={`relative w-[44px] h-[44px] rounded-full flex items-center justify-center border-2 transition-all duration-700 ${isActive ? 'scale-105' : 'scale-100'}`}
                        style={{ 
                          borderColor: isActive ? `${colors.primary}80` : `${colors.primary}30`,
                          background: isDark ? '#0f0f0f' : '#f8f9fa',
                          boxShadow: isActive 
                            ? `0 0 25px ${colors.primary}50`
                            : `0 0 8px ${colors.primary}20`
                        }}
                      >
                        <span 
                          className="text-lg font-bold transition-all duration-700"
                          style={{ 
                            color: isActive ? colors.primary : `${colors.primary}60`,
                            textShadow: isActive ? `0 0 15px ${colors.primary}` : 'none'
                          }}
                        >
                          {step.step}
                        </span>
                      </div>
                    </div>

                    {/* Connecting line to next step */}
                    {index < processSteps.length - 1 && (
                      <div className="relative w-[2px] flex-1 mt-3 mb-1 min-h-[60px]">
                        {/* Background line - fades at both ends */}
                        <div 
                          className="absolute inset-0 rounded-full"
                          style={{
                            background: isDark 
                              ? 'linear-gradient(to bottom, transparent, rgba(123, 167, 194, 0.25) 25%, rgba(123, 167, 194, 0.25) 75%, transparent)'
                              : 'linear-gradient(to bottom, transparent, rgba(90, 139, 166, 0.2) 25%, rgba(90, 139, 166, 0.2) 75%, transparent)'
                          }}
                        />
                        {/* Animated fill */}
                        <div 
                          className="absolute top-0 left-0 w-full rounded-full transition-all duration-500 ease-out overflow-hidden"
                          style={{ 
                            height: `${segmentProgress}%`,
                          }}
                        >
                          <div 
                            className="absolute inset-0"
                            style={{
                              background: `linear-gradient(to bottom, transparent, ${colors.primary} 25%, ${glowColors[index + 1]?.primary || colors.secondary} 75%, transparent)`,
                              boxShadow: segmentProgress > 0 ? `0 0 10px ${colors.primary}` : 'none'
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="pt-3 pb-4 flex-1">
                    <h3 
                      className={`text-lg font-semibold mb-2 transition-all duration-700 ${
                        isActive 
                          ? isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'
                          : isDark ? 'text-white/60' : 'text-gray-500'
                      }`}
                    >
                      {translatedTitleMobile}
                    </h3>
                    <p className={`text-sm leading-relaxed transition-all duration-700 ${
                      isActive 
                        ? isDark ? 'text-gray-400' : 'text-gray-600'
                        : isDark ? 'text-gray-600' : 'text-gray-400'
                    }`}>
                      {translatedDescMobile}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
